import { useState } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Search } from "lucide-react"
import { ThemeToggle } from "@/components/theme-toggle"
import { LanguageSwitcher } from "@/components/language-switcher"
import { Input } from "@/components/ui/input"
import { useAuth } from "@/components/auth-provider"
import { CommandPalette } from "@/components/app/CommandPalette"
import { AppSidebar } from "@/components/app/AppSidebar"
import { PaymentGraceBanner } from "@/components/app/PaymentGraceBanner"
import axios from "axios"
import { API_BASE } from "@/config/api"
import { CLINICAL_VERTICALS, getSessionVertical, labelForVertical } from "@/config/clinical-verticals"
import { useClinicalContext } from "@/hooks/use-clinical-context"
import { hasFeature } from "@/config/entitlements"

export default function AppLayout({ children, title, actions }) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [cmdOpen, setCmdOpen] = useState(false)
  const [searchQ, setSearchQ] = useState("")
  const { enabledVerticals, effectiveVertical, setWorkingVertical } = useClinicalContext()
  const showSite = hasFeature(user, "multi_location") && user?.facility_name

  const handleSignOut = async () => {
    await logout()
    navigate("/login", { replace: true })
  }

  const initials = (user?.name || "DR").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()

  const onSearch = async (e) => {
    if (e.key !== "Enter" || !searchQ.trim()) return
    try {
      const { data } = await axios.get(`${API_BASE}/search`, { params: { q: searchQ } })
      if (data.clients?.[0]) navigate(`/app/clients?open=${data.clients[0].id}`)
      else if (data.notes?.[0]) navigate(`/app/notes?note=${data.notes[0].id}`)
    } catch { /* ignore */ }
  }

  return (
    <div className="min-h-screen bg-background flex">
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />

      <AppSidebar onSignOut={handleSignOut} />

      <div className="flex-1 min-w-0 flex flex-col">
        <PaymentGraceBanner />
        <header className="h-14 border-b border-border flex items-center px-4 lg:px-8 gap-3" data-testid="app-topbar">
          <div className="relative max-w-md w-full">
            <Search strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("app.searchPlaceholder")}
              className="pl-9 h-9 rounded-full bg-muted/60 border-transparent focus-visible:border-border"
              data-testid="topbar-search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={onSearch}
            />
          </div>
          <button type="button" onClick={() => setCmdOpen(true)} className="hidden sm:block text-xs text-muted-foreground px-2 py-1 rounded border border-border">
            Ctrl+K
          </button>
          <div className="ml-auto flex items-center gap-2">
            {showSite && (
              <Link
                to="/app/network"
                className="hidden sm:flex items-center gap-1.5 h-9 text-xs rounded-full border border-border bg-background px-3 hover:bg-muted transition-colors max-w-[200px] truncate"
                title={t("app.siteLabel", { defaultValue: "Locație activă" })}
              >
                <span className="text-muted-foreground shrink-0">{t("app.siteLabel", { defaultValue: "Locație" })}</span>
                <span className="font-medium truncate">{user.facility_name}</span>
              </Link>
            )}
            <Link
              to="/app/settings?tab=profile"
              className="hidden sm:flex items-center gap-1.5 h-9 text-xs rounded-full border border-border bg-background px-3 hover:bg-muted transition-colors max-w-[220px] truncate"
              title={t("app.specialtyEditTitle")}
            >
              <span className="text-muted-foreground shrink-0">{t("app.specialtyLabel")}</span>
              <span className="font-medium truncate">{labelForVertical(getSessionVertical() || effectiveVertical)}</span>
            </Link>
            {enabledVerticals.length > 1 && (
              <select
                className="hidden md:block h-9 text-xs rounded-full border border-border bg-background px-2 max-w-[160px]"
                value={getSessionVertical() || effectiveVertical}
                onChange={(e) => setWorkingVertical(e.target.value)}
                title={t("app.verticalQuickSwitchTitle")}
              >
                {CLINICAL_VERTICALS.filter((v) => enabledVerticals.includes(v.value)).map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            )}
            <LanguageSwitcher />
            <ThemeToggle />
            <div className="h-9 w-9 rounded-full bg-secondary grid place-items-center text-sm font-medium" data-testid="topbar-avatar" title={user?.name}>
              {initials}
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 lg:px-8 py-8">
          {(title || actions) && (
            <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
              <div>{title && <h1 className="font-display text-3xl tracking-tight font-medium">{title}</h1>}</div>
              <div className="flex items-center gap-2">{actions}</div>
            </div>
          )}
          {children}
        </main>
      </div>
    </div>
  )
}
